/**
 * Sliding cart drawer: Ajax Cart API, header badge sync, a11y open/close.
 */
(function () {
  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  drawer.removeAttribute('hidden');
  drawer.setAttribute('aria-hidden', 'true');

  var panel = drawer.querySelector('.sigma-cart-drawer__panel');
  var content = drawer.querySelector('[data-cart-drawer-content]');
  var footer = drawer.querySelector('[data-cart-drawer-footer]');
  var loadingEl = drawer.querySelector('[data-cart-drawer-loading]');
  var subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
  var i18nEl = drawer.querySelector('[data-cart-drawer-i18n]');
  var openers = document.querySelectorAll('[data-cart-drawer-open]');

  var cartJsUrl = drawer.getAttribute('data-cart-api-url') || '/cart.js';
  var cartChangeUrl = drawer.getAttribute('data-cart-change-url') || '/cart/change.js';

  var lastFocus = null;
  var currency = drawer.getAttribute('data-shop-currency') || 'USD';
  var ariaEmpty = drawer.getAttribute('data-aria-empty') || 'Cart';
  var ariaWithCount = drawer.getAttribute('data-aria-with-count') || 'Cart, [count] items';

  /** Blocks overlapping /cart/change.js calls (rapid remove or qty changes). */
  var isMutating = false;

  /** Nested fetches (e.g. refresh + change) keep the spinner until all complete. */
  var loadingDepth = 0;

  /** In-flight GET /cart.js from refresh() — aborted when a line change starts to avoid stale overwrites. */
  var cartGetAbort = null;

  function abortPendingCartGet() {
    if (cartGetAbort) {
      try {
        cartGetAbort.abort();
      } catch (abortErr) {
        /* ignore */
      }
      cartGetAbort = null;
    }
  }

  var i18n = {};
  if (i18nEl && i18nEl.textContent) {
    try {
      i18n = JSON.parse(i18nEl.textContent);
    } catch (e) {
      i18n = {};
    }
  }

  function formatMoney(cents, curr) {
    var c = curr || currency;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: c,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function setHeaderCartState(count) {
    openers.forEach(function (opener) {
      var useMobileBadge = opener.classList.contains('mobile-appbar__cart');
      var badgeClass = useMobileBadge ? 'mobile-appbar__cart-badge' : 'sigma-header__cart-badge';
      var allBadges = opener.querySelectorAll('.sigma-header__cart-badge, .mobile-appbar__cart-badge');
      var badge = null;
      allBadges.forEach(function (el) {
        if (el.classList.contains(badgeClass)) {
          if (!badge) {
            badge = el;
          } else {
            el.remove();
          }
        } else {
          el.remove();
        }
      });
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = badgeClass;
          badge.setAttribute('aria-hidden', 'true');
          opener.appendChild(badge);
        }
        badge.textContent = String(count);
        badge.removeAttribute('hidden');
        opener.setAttribute('aria-label', ariaWithCount.replace('[count]', String(count)));
      } else {
        if (badge) {
          badge.setAttribute('hidden', '');
        }
        opener.setAttribute('aria-label', ariaEmpty);
      }
    });
  }

  function setLoading(on) {
    if (!loadingEl) return;
    if (on) {
      loadingDepth++;
      if (loadingDepth === 1) {
        loadingEl.removeAttribute('hidden');
      }
    } else {
      loadingDepth = Math.max(0, loadingDepth - 1);
      if (loadingDepth === 0) {
        loadingEl.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Disable line controls and checkout while a cart mutation is in flight (DOM is replaced on success).
   */
  function setDrawerBusy(disabled) {
    if (content) {
      content.querySelectorAll('.sigma-cart-drawer__qty, .sigma-cart-drawer__remove').forEach(function (el) {
        el.disabled = !!disabled;
        el.setAttribute('aria-busy', disabled ? 'true' : 'false');
      });
    }
    var checkout = drawer.querySelector('.sigma-cart-drawer__checkout');
    if (checkout) {
      checkout.disabled = !!disabled;
    }
  }

  function renderEmpty() {
    footer.hidden = true;
    content.innerHTML =
      '<div class="sigma-cart-drawer__empty">' +
      '<p class="sigma-cart-drawer__empty-title">' +
      escapeHtml(i18n.empty_title || '') +
      '</p>' +
      '<p class="sigma-cart-drawer__empty-text">' +
      escapeHtml(i18n.empty_body || '') +
      '</p>' +
      '<a class="sigma-cart-drawer__empty-link" href="' +
      escapeHtml(drawer.getAttribute('data-all-products-url') || '/') +
      '">' +
      escapeHtml(i18n.continue_shopping || '') +
      '</a>' +
      '</div>';
  }

  function renderCart(cart) {
    currency = cart.currency || currency;
    setHeaderCartState(cart.item_count);

    if (!cart.items || cart.items.length === 0) {
      renderEmpty();
      return;
    }

    footer.hidden = false;
    subtotalEl.textContent = formatMoney(cart.total_price, currency);

    var html = '<ul class="sigma-cart-drawer__lines" role="list">';
    cart.items.forEach(function (item) {
      var imgUrl = '';
      if (typeof item.image === 'string') {
        imgUrl = item.image;
      } else if (item.image && item.image.url) {
        imgUrl = item.image.url;
      } else if (item.featured_image && item.featured_image.url) {
        imgUrl = item.featured_image.url;
      }
      var title = escapeHtml(item.title || '');
      var variant =
        item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="sigma-cart-drawer__variant">' + escapeHtml(item.variant_title) + '</p>'
          : '';
      var unit = formatMoney(item.final_price, currency);
      var lineTotal = formatMoney(item.final_line_price, currency);
      var showCompare =
        item.original_price &&
        item.final_price &&
        item.original_price > item.final_price;
      var unitHtml = showCompare
        ? '<s class="sigma-cart-drawer__compare">' +
          formatMoney(item.original_price, currency) +
          '</s> ' +
          '<span>' +
          unit +
          '</span>'
        : '<span>' + unit + '</span>';

      html += '<li class="sigma-cart-drawer__line" role="listitem" data-line-key="' + escapeHtml(item.key) + '">';
      html += '<div class="sigma-cart-drawer__line-media">';
      if (imgUrl) {
        html +=
          '<a href="' +
          escapeHtml(item.url) +
          '" tabindex="-1" aria-hidden="true" class="sigma-cart-drawer__thumb-link">' +
          '<img src="' +
          escapeHtml(imgUrl) +
          '" alt="" class="sigma-cart-drawer__thumb" width="96" height="96" loading="lazy">' +
          '</a>';
      } else {
        html += '<div class="sigma-cart-drawer__thumb sigma-cart-drawer__thumb--placeholder"></div>';
      }
      html += '</div>';

      html += '<div class="sigma-cart-drawer__line-body">';
      html +=
        '<a href="' +
        escapeHtml(item.url) +
        '" class="sigma-cart-drawer__line-title">' +
        title +
        '</a>';
      html += variant;
      html += '<div class="sigma-cart-drawer__line-meta">';
      html +=
        '<span class="sigma-cart-drawer__line-unit" data-label="' +
        escapeHtml(i18n.heading_price || '') +
        '">' +
        unitHtml +
        '</span>';
      html +=
        '<label class="sigma-cart-drawer__qty-wrap"><span class="sigma-cart-drawer__qty-label">' +
        escapeHtml(i18n.heading_quantity || '') +
        '</span>';
      html +=
        '<input class="sigma-cart-drawer__qty" type="number" min="0" step="1" value="' +
        Number(item.quantity) +
        '" aria-label="' +
        escapeHtml((i18n.heading_quantity || '') + ' ' + title) +
        '" data-line-key="' +
        escapeHtml(item.key) +
        '">';
      html += '</label>';
      html +=
        '<span class="sigma-cart-drawer__line-total" data-label="' +
        escapeHtml(i18n.heading_total || '') +
        '">' +
        lineTotal +
        '</span>';
      html += '</div>';
      html +=
        '<button type="button" class="sigma-cart-drawer__remove" data-remove-key="' +
        escapeHtml(item.key) +
        '">' +
        escapeHtml(i18n.remove || '') +
        '</button>';
      html += '</div></li>';
    });
    html += '</ul>';
    content.innerHTML = html;
  }

  function fetchCart(opts) {
    var signal = opts && opts.signal;
    return fetch(cartJsUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: signal,
    }).then(function (r) {
      if (!r.ok) throw new Error('Cart fetch failed');
      return r.json();
    });
  }

  /**
   * Update or remove a line. Shopify /cart/change.js expects `id` (variant id or line item key string)
   * or `line` (1-based index). We use `id` + line item key so split lines (same variant, different props) stay correct.
   * @param {string} lineItemKey - Cart line `key` from /cart.js
   * @param {number} quantity - New quantity (0 removes the line)
   */
  function changeLine(lineItemKey, quantity) {
    if (!lineItemKey || isMutating) {
      return Promise.resolve();
    }

    var qty = parseInt(String(quantity), 10);
    if (isNaN(qty) || qty < 0) {
      return Promise.resolve();
    }

    abortPendingCartGet();

    isMutating = true;
    setLoading(true);
    setDrawerBusy(true);

    return fetch(cartChangeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: lineItemKey, quantity: qty }),
    })
      .then(function (r) {
        if (!r.ok) {
          return r
            .json()
            .catch(function () {
              return { message: r.statusText || 'Change failed' };
            })
            .then(function (body) {
              var msg = (body && body.description) || (body && body.message) || 'Cart change failed';
              throw new Error(msg);
            });
        }
        return r.json();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      })
      .catch(function (err) {
        console.warn('[sigma-cart-drawer] Cart change failed:', err && err.message ? err.message : err);
        return fetchCart()
          .then(function (cart) {
            renderCart(cart);
            return cart;
          })
          .catch(function (fetchErr) {
            if (fetchErr && fetchErr.name === 'AbortError') return;
            console.warn('[sigma-cart-drawer] Cart refresh after error failed:', fetchErr);
          });
      })
      .finally(function () {
        setLoading(false);
        isMutating = false;
        setDrawerBusy(false);
      });
  }

  function refresh() {
    abortPendingCartGet();
    cartGetAbort = new AbortController();
    var signal = cartGetAbort.signal;
    var thisController = cartGetAbort;

    setLoading(true);
    return fetchCart({ signal: signal })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      })
      .catch(function (e) {
        if (e && e.name === 'AbortError') return;
        console.warn('[sigma-cart-drawer] Cart refresh failed:', e);
      })
      .finally(function () {
        if (thisController === cartGetAbort) {
          cartGetAbort = null;
        }
        setLoading(false);
      });
  }

  function openDrawer() {
    if (drawer.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sigma-cart-drawer-open');

    refresh().then(function () {
      var closeBtn = drawer.querySelector('.sigma-cart-drawer__close');
      if (closeBtn && typeof closeBtn.focus === 'function') {
        closeBtn.focus();
      } else if (panel && typeof panel.focus === 'function') {
        panel.focus();
      }
    });
  }

  function closeDrawer() {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sigma-cart-drawer-open');
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
  }

  function onContentClick(e) {
    var btn =
      e.target &&
      e.target.closest &&
      e.target.closest('button.sigma-cart-drawer__remove[data-remove-key]');
    if (!btn) return;
    if (isMutating) return;
    e.preventDefault();
    var key = btn.getAttribute('data-remove-key');
    if (key) changeLine(key, 0);
  }

  function onContentChange(e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('input.sigma-cart-drawer__qty')) return;
    if (isMutating) return;
    var key = t.getAttribute('data-line-key');
    var raw = String(t.value).trim();
    if (raw === '') return;
    var qty = parseInt(raw, 10);
    if (!key || isNaN(qty)) return;
    changeLine(key, Math.max(0, qty));
  }

  openers.forEach(function (opener) {
    opener.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        return;
      }
      e.preventDefault();
      openDrawer();
    });
  });

  drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (el) {
    el.addEventListener('click', function () {
      closeDrawer();
    });
  });

  content.addEventListener('click', onContentClick);
  content.addEventListener('change', onContentChange);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  fetchCart({})
    .then(function (cart) {
      setHeaderCartState(cart.item_count);
    })
    .catch(function () {});
})();
